/**
 * Keyboard-shortcut notation: `['Mod', 'K']` reads „⌘ K“ on a Mac and
 * „Strg + K“ elsewhere. `Mod` is the platform's command key.
 */

/** One key combination, pressed together: `['Mod', 'Shift', 'P']`. */
export type NxKeyCombo = readonly string[];

/** Whether the reader is on an Apple platform (⌘ instead of Strg). */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform ?? nav.platform ?? '';
  return /mac|iphone|ipad|ipod/i.test(platform) || /Mac OS X/.test(nav.userAgent ?? '');
}

const APPLE: Record<string, string> = {
  mod: '⌘',
  meta: '⌘',
  cmd: '⌘',
  ctrl: '⌃',
  control: '⌃',
  alt: '⌥',
  option: '⌥',
  shift: '⇧',
  enter: '↩',
  return: '↩',
  backspace: '⌫',
  delete: '⌦',
  tab: '⇥',
};

const OTHER: Record<string, string> = {
  mod: 'Strg',
  meta: 'Win',
  cmd: 'Strg',
  ctrl: 'Strg',
  control: 'Strg',
  alt: 'Alt',
  option: 'Alt',
  shift: '⇧',
  enter: '↵',
  return: '↵',
  backspace: '⌫',
  delete: 'Entf',
  tab: 'Tab',
};

const COMMON: Record<string, string> = {
  escape: 'Esc',
  esc: 'Esc',
  space: 'Leertaste',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  pageup: 'Bild ↑',
  pagedown: 'Bild ↓',
  home: 'Pos1',
  end: 'Ende',
};

/** Label of one key for the given platform; letters are upper-cased. */
export function keyLabel(key: string, apple = isApplePlatform()): string {
  const id = key.toLowerCase();
  const label = (apple ? APPLE : OTHER)[id] ?? COMMON[id];
  if (label) return label;
  return key.length === 1 ? key.toUpperCase() : key;
}

/**
 * A combo from its written form: an array as is, a string split at `+`
 * („Mod+K“). A lone `+` stays the plus key.
 */
export function parseCombo(keys: NxKeyCombo | string): string[] {
  if (typeof keys !== 'string') return [...keys];
  if (keys.trim() === '+') return ['+'];
  return keys
    .split(/\s*\+\s*/)
    .map((k) => k.trim())
    .filter(Boolean);
}

/** A combo as plain text for a title or an accessible name: „Strg + K“, „⌘K“. */
export function formatCombo(keys: NxKeyCombo | string, apple = isApplePlatform()): string {
  const labels = parseCombo(keys).map((k) => keyLabel(k, apple));
  return labels.join(apple ? '' : ' + ');
}

/** Keys that are only a modifier: pressing one alone records nothing. */
const MODIFIER_KEYS: ReadonlySet<string> = new Set([
  'Shift',
  'Control',
  'Alt',
  'AltGraph',
  'Meta',
  'OS',
  'Fn',
  'FnLock',
  'CapsLock',
  'NumLock',
  'ScrollLock',
  'Hyper',
  'Super',
  'Symbol',
  'SymbolLock',
]);

/** Event key names that differ from the kit's notation. */
const EVENT_KEY_NAMES: Record<string, string> = {
  ' ': 'Space',
  '+': 'Plus',
  Esc: 'Escape',
  Del: 'Delete',
  Up: 'ArrowUp',
  Down: 'ArrowDown',
  Left: 'ArrowLeft',
  Right: 'ArrowRight',
};

/** The named keys of the notation (besides one character and F1–F12). */
const NAMED_EVENT_KEYS: ReadonlySet<string> = new Set([
  'Space',
  'Plus',
  'Enter',
  'Escape',
  'Tab',
  'Backspace',
  'Delete',
  'Insert',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

/** Whether a key name can be written in a combo: one character, F1–F12 or a named key. */
function inNotation(key: string): boolean {
  if ([...key].length === 1) return key.trim() !== '';
  return /^F([1-9]|1[0-2])$/.test(key) || NAMED_EVENT_KEYS.has(key);
}

/**
 * The key of a key event in the kit's notation, or null for a lone modifier,
 * IME composition, a dead key (`Dead`: ⌥E on a Mac waits for a vowel) and any
 * key the notation has no name for (`ContextMenu`, `PrintScreen`, media keys,
 * F13 …) — the press is not a combo, so a recorder keeps listening. A letter
 * or digit is what the cap shows (`event.key`) — „Z“ on a German keyboard
 * whatever its position — and the physical key (`event.code`) where the layout
 * types something else: ⌥S on a Mac types „ß“, Shift+1 types „!“, and both are
 * recorded as `S` and `1`. Named keys (`F5`, `Enter`, `ArrowUp`) come from
 * `event.key`.
 */
function keyOfEvent(event: KeyboardEvent): string | null {
  const key = event.key ?? '';
  if (!key || key === 'Process' || key === 'Unidentified' || key === 'Dead' || event.isComposing) {
    return null;
  }
  if (MODIFIER_KEYS.has(key)) return null;
  if (/^[a-z0-9]$/i.test(key)) return key.toUpperCase();
  const code = event.code ?? '';
  // Only where the layout typed a character: a named key keeps its own name.
  if ([...key].length === 1) {
    if (/^Key[A-Z]$/.test(code)) return code.slice(3);
    if (/^Digit\d$/.test(code)) return code.slice(5);
  }
  const name = EVENT_KEY_NAMES[key] ?? key;
  return inNotation(name) ? name : null;
}

/**
 * A key press as a combo in the kit's notation (`'Mod+Shift+F'`): ⌘ is `Mod`
 * and ⌃ is `Ctrl` on an Apple platform; elsewhere Ctrl is `Mod` and the
 * Win/Meta key is `Meta`; then `Alt`, `Shift` and the key. Null for a lone
 * modifier — the press is not finished yet.
 */
export function comboFromKeyEvent(event: KeyboardEvent, apple = isApplePlatform()): string | null {
  const key = keyOfEvent(event);
  if (key === null) return null;
  const parts: string[] = [];
  if (apple) {
    if (event.metaKey) parts.push('Mod');
    if (event.ctrlKey) parts.push('Ctrl');
  } else {
    if (event.ctrlKey) parts.push('Mod');
    if (event.metaKey) parts.push('Meta');
  }
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');
  parts.push(key);
  return parts.join('+');
}
